import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { Link, Stack, useRouter, usePathname } from 'expo-router';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useAuthStore } from '@/store/auth';
import { useModalStore } from '@/store/modal';
import useResponsiveness from '@/hooks/useResponsiveness';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import NavMegaMenu from '@/components/Navigation/NavMegaMenu';
import NavDropdownLink from '@/components/Navigation/NavDropdownLink';
import useUseCaseMenu from '@/hooks/useUseCaseMenu';
import useNavMegaMenuGroup from '@/hooks/useNavMegaMenuGroup';
import {
  FEATURES_NAV_COLUMNS,
  FEATURES_NAV_HUB,
  DOCS_NAV_COLUMNS,
  DOCS_NAV_HUB,
  isFeaturesNavPath,
  isDocsNavPath,
} from '@/constants/publicNavLinks';
import { useTheme } from '@/context/ThemeContext';
import useGlobalStyles from '@/hooks/useGlobalStyles';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Button from '@/components/Buttons/Button';
import { trackMarketingEvent, MARKETING_ACTION } from '@/constants/utils/analyticsEvents';
import ThemeSelector from '@/components/Navigation/ThemeSelector';
import Logo from '@/components/Icons/Logo';
import Icon from '@/components/Icon';
import ModalRoot from '@/components/Modal/ModalRoot';
import ToastRoot from '@/components/ToastRoot';
import ExploreLightboxHost from '@/components/Explore/ExploreLightboxHost';
import { isDarkTheme } from '@/constants/Colors';
import { BlurView } from 'expo-blur';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ACCENT, LAYOUT, landingPalette } from '@/components/Landing/landingTokens';
import { pricingPalette } from '@/components/Pricing/pricingPalette';
import { apiPalette } from '@/components/Api/apiTokens';

/** Shared padding for Home / Pricing / About and mega-menu triggers. */
/** Nav pill label weight — 500 across every public header control. */
const PUBLIC_NAV_TEXT_WEIGHT = 'medium';

function getPublicNavButtonStyle({ isSM, isMD }) {
  return {
    minWidth: isMD ? 0 : 52,
    paddingHorizontal: isSM ? 8 : isMD ? 10 : 12,
  };
}

import '../styles/dropdown.css';

/**
 * The landing page scrolls inside its own container rather than the document.
 * React Native Web drops `className` on `ScrollView`, so the container is
 * tagged with a `data-*` attribute instead.
 */
const scrollLandingToTop = () => {
  const scroller = document.querySelector('[data-landing-scroll]');
  (scroller || window).scrollTo({ top: 0, behavior: 'smooth' });
};

// Create styles outside component
const createStyles = (colors, isXS, isSM, isMD) => StyleSheet.create({
  leftContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 0,
    flexShrink: 1,
    minWidth: 0,
  },
  rightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isSM ? 1 : 2,
    paddingRight: 0,
    flexShrink: 0,
  },
  logo: {
    // Brand minimum, also used by the in-app navigation rail.
    width: 24,
    height: 24,
  },
  navLinks: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    minWidth: 0,
    gap: 2,
    display: isSM ? 'none' : 'flex',
  },
  logoWordmark: {
    flexShrink: 0,
    // Match SidebarHeader's system-font wordmark.
    lineHeight: 24,
    letterSpacing: -0.8,
  },
  logoLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginRight: isSM ? 8 : isMD ? 12 : 14,
    minHeight: 44,
    paddingHorizontal: 6,
    flexShrink: 0,
  },
  menuButton: {
    display: isXS ? 'flex' : 'none',
    padding: 8,
    marginLeft: 4,
    borderRadius: 9999,
  },
  menuButtonHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  menuItem: {
    paddingVertical: 8,
    paddingHorizontal: 15,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  preferencesContainer: {
    paddingBottom: 4,
    paddingLeft: 5,
  },
  preferenceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 0,
  },
  preferenceLabel: {
    marginRight: 8,
  },
  signUpText: {
    color: colors.accent_text_color,
  },
  tabsContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    marginBottom: 8,
    padding: 4,
    gap: 4,
  },
  colorGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    padding: 8,
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  colorOption: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  colorOptionActive: {
    borderWidth: 2,
    borderColor: colors.text_color_primary,
  },
  colorWrapper: {
    alignItems: 'center',
    width: 'calc(20% - 6.4px)',  // Exactly 5 per row accounting for gap
  },
  sectionTitle: {
    paddingHorizontal: 8,
  },
  exampleButtonContainer: {
    paddingHorizontal: 8,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    marginTop: 16,
  },
  exampleButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// Memoized navigation buttons left component
const NavigationButtonsLeft = React.memo(() => {
  const { colors, theme } = useTheme();
  const pathname = usePathname();
  const isLandingPage = pathname === '/';
  const logoColor = pathname === '/pricing' ? pricingPalette(isDarkTheme(theme)).ink : isDarkTheme(theme) ? '#FFFFFF' : ACCENT;
  const { t } = useTranslation();
  const { isXS, isSM, isMD } = useResponsiveness();
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const isAuthenticated = token && !isTokenExpired();
  
  const styles = useMemo(() => createStyles(colors, isXS, isSM, isMD), [colors, isXS, isSM, isMD]);
  const navButtonStyle = useMemo(
    () => getPublicNavButtonStyle({ isSM, isMD }),
    [isSM, isMD],
  );
  const navButtonSize = isSM ? 'small' : 'medium';

  const { groups: useCaseGroups, isLoading: useCasesLoading } = useUseCaseMenu();
  const {
    isOpen: isMegaMenuOpen,
    openMenu: openMegaMenu,
    scheduleClose: scheduleMegaMenuClose,
    closeMenu: closeMegaMenu,
    closeAll: closeAllMegaMenus,
  } = useNavMegaMenuGroup();
  const useCaseMegaColumns = useMemo(
    () => useCaseGroups.map((group) => ({
      label: group.label,
      items: group.items.map((item) => ({
        href: item.href,
        label: item.name,
      })),
    })),
    [useCaseGroups],
  );

  const menuItems = useMemo(() => [
    { label: t('navigation.home'), path: '/' },
    { label: t('common.pricing'), path: '/pricing' },
    { label: 'Business', path: '/business' },
    { label: t('common.about'), path: '/about' },
    { label: 'Learn', path: '/learn' },
    ...(isLandingPage ? [{ label: 'Docs', path: DOCS_NAV_HUB }] : []),
  ], [t, isLandingPage]);

  /**
   * Home and the logo are real links now, so navigation is the browser's job.
   * This only preserves the one extra behaviour they had: when you are already
   * on the landing page, clicking Home scrolls to the top instead of doing a
   * no-op navigation. `Link` runs the child's `onPress` first, so cancelling
   * the event here stops the router from re-navigating to the current page.
   */
  const maybeScrollHomeToTop = useCallback((event) => {
    if (pathname !== '/' || Platform.OS !== 'web') return;
    event?.preventDefault?.();
    scrollLandingToTop();
  }, [pathname]);

  useEffect(() => {
    closeAllMegaMenus();
  }, [pathname, closeAllMegaMenus]);

  return (
    <View style={styles.leftContainer}>
      {/* `Link asChild` hands `href` to the Pressable, and React Native Web
          renders any View carrying an `href` as a real `<a>`. Without it these
          are `<div role="link">` and the nav passes no link equity at all. */}
      <Link href="/" asChild>
        <Pressable
          style={styles.logoLink}
          onPress={maybeScrollHomeToTop}
          accessibilityRole="link"
          accessibilityLabel="Gab AI home"
        >
          <Logo 
            width={styles.logo.width}
            height={styles.logo.height}
            color={logoColor}
          />
          <Text
            size="xl"
            weight="bold"
            numberOfLines={1}
            style={[styles.logoWordmark, { color: logoColor }]}
          >
            Gab AI
          </Text>
        </Pressable>
      </Link>

      {/* Desktop Navigation */}
      <View style={styles.navLinks}>
        {menuItems.map((item) => (
          <Link key={item.path} href={item.path} asChild>
            <NavigationBarButton 
              label={item.label}
              isRound={true}
              size={navButtonSize}
              textWeight={PUBLIC_NAV_TEXT_WEIGHT}
              variant={pathname !== '/' && pathname === item.path ? 'navActive' : 'nav'}
              dataSet={{ publicNavButton: 'true' }}
              onPress={item.path === '/' ? maybeScrollHomeToTop : undefined}
              style={navButtonStyle}
            />
          </Link>
        ))}
        {!isLandingPage && (
          <>
            <NavMegaMenu
              menuId="docs"
              triggerLabel="Docs"
              triggerHref={DOCS_NAV_HUB}
              isActive={isDocsNavPath(pathname)}
              columns={DOCS_NAV_COLUMNS}
              footer={{ href: DOCS_NAV_HUB, label: 'Browse all docs' }}
              triggerButtonStyle={navButtonStyle}
              triggerButtonSize={navButtonSize}
              triggerTextWeight={PUBLIC_NAV_TEXT_WEIGHT}
              open={isMegaMenuOpen('docs')}
              onOpen={() => openMegaMenu('docs')}
              onScheduleClose={() => scheduleMegaMenuClose('docs')}
              onClose={() => closeMegaMenu('docs')}
            />
          </>
        )}
        {!isLandingPage && (
          <>
            <NavMegaMenu
              menuId="features"
              triggerLabel="Features"
              triggerHref={FEATURES_NAV_HUB}
              isActive={isFeaturesNavPath(pathname)}
              columns={FEATURES_NAV_COLUMNS}
              footer={{ href: FEATURES_NAV_HUB, label: 'Explore all features' }}
              triggerButtonStyle={navButtonStyle}
              triggerButtonSize={navButtonSize}
              triggerTextWeight={PUBLIC_NAV_TEXT_WEIGHT}
              open={isMegaMenuOpen('features')}
              onOpen={() => openMegaMenu('features')}
              onScheduleClose={() => scheduleMegaMenuClose('features')}
              onClose={() => closeMegaMenu('features')}
            />
            <NavMegaMenu
              menuId="use-cases"
              triggerLabel="Use cases"
              triggerHref="/use-cases"
              isActive={pathname === '/use-cases' || pathname?.startsWith('/use-cases/')}
              columns={useCaseMegaColumns}
              isLoading={useCasesLoading}
              footer={{ href: '/use-cases', label: 'View all use cases' }}
              triggerButtonStyle={navButtonStyle}
              triggerButtonSize={navButtonSize}
              triggerTextWeight={PUBLIC_NAV_TEXT_WEIGHT}
              open={isMegaMenuOpen('use-cases')}
              onOpen={() => openMegaMenu('use-cases')}
              onScheduleClose={() => scheduleMegaMenuClose('use-cases')}
              onClose={() => closeMegaMenu('use-cases')}
            />
          </>
        )}
      </View>


    </View>
  );
});

NavigationButtonsLeft.displayName = 'NavigationButtonsLeft';

// Memoized navigation buttons right component
const NavigationButtonsRight = React.memo(() => {
  const { colors, theme } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const isLandingPage = pathname === '/';
  const { isXS, isSM, isMD, windowWidth } = useResponsiveness();
  const { openModal } = useModalStore(state => state.actions);
  const { t } = useTranslation();
  const token = useAuthStore(state => state.token);
  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const isAuthenticated = token && !isTokenExpired();
  const [isOpen, setIsOpen] = useState(false);
  
  const menuItems = useMemo(() => [
    { label: t('navigation.home'), path: '/' },
    { label: t('common.pricing'), path: '/pricing' },
    { label: 'Business', path: '/business' },
    { label: t('common.about'), path: '/about' },
    { label: 'Learn', path: '/learn' },
    ...(isLandingPage ? [{ label: 'Docs', path: DOCS_NAV_HUB }] : []),
  ], [t, isLandingPage]);

  const legalMenuItems = useMemo(() => [
    { label: t('common.termsOfSale'), path: '/terms-of-sale' },
    { label: t('common.termsOfService'), path: '/terms-of-service' },
    { label: t('common.privacyPolicy'), path: '/privacy-policy' },
  ], [t]);

  // Prefetched on mount — same cache as the desktop mega menu.
  const { groups: useCaseGroups } = useUseCaseMenu();
  const useCaseMenuItems = useMemo(
    () => useCaseGroups.flatMap((group) => group.items),
    [useCaseGroups],
  );

  const closeMobileMenu = useCallback(() => setIsOpen(false), []);

  const handleNavigation = useCallback((path) => {
    // If we're on the landing page and clicking Home, scroll to top
    if (path === '/' && pathname === '/' && Platform.OS === 'web') {
      scrollLandingToTop();
      return;
    }
    router.push(path);
  }, [router, pathname]);

  // Close dropdown when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const styles = useMemo(() => createStyles(colors, isXS, isSM, isMD), [colors, isXS, isSM, isMD]);

  const handleAuth = useCallback((type) => {
    if (Platform.OS === 'web' && !isXS) {
      openModal(type === 'login' ? 'LoginModal' : 'SignupModal');
    } else {
      router.push(`/${type}`);
    }
  }, [isXS, openModal, router]);

  return (
    <View style={styles.rightContainer}>
      {!isSM && (
        <>
          {!isLandingPage && <ThemeSelector showAccentSelector={false} />}
          <Button
            text={isMD ? undefined : 'Download'}
            prependIcon="download"
            tooltip="Download Gab AI"
            accessibilityLabel="Download Gab AI"
            variant="nav"
            size={isSM ? 'small' : 'medium'}
            textWeight={PUBLIC_NAV_TEXT_WEIGHT}
            onPress={() => openModal('DownloadAppsModal')}
            isRound={true}
            dataSet={{ publicNavButton: 'true' }}
          />
          {
            !isAuthenticated &&
            <Button
              text={t('common.signIn')}
              variant="nav"
              size={isSM ? 'small' : 'medium'}
              textWeight={PUBLIC_NAV_TEXT_WEIGHT}
              onPress={() => handleAuth('login')}
              isRound={true}
              dataSet={{ publicNavButton: 'true' }}
            />
          }
        </>
      )}

      {
        isSM && (
          /* Compact navigation also leaves room for auth actions on tablets. */
          <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenu.Trigger asChild>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Open navigation menu"
                dataSet={{ publicNavButton: 'true' }}
                style={{ width: 40, height: 40, borderRadius: 20, alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              >
                <Icon name="bars" size={15} color={colors.text_color_primary} solid />
              </Pressable>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="DropdownMenuContent">
              <DropdownMenuItem
                title="Download Gab AI"
                icon="download"
                onSelect={() => { closeMobileMenu(); openModal('DownloadAppsModal'); }}
              />
              {menuItems.map((item) => (
                <NavDropdownLink
                  key={item.path}
                  href={item.path}
                  label={item.label}
                  isActive={pathname === item.path}
                  onNavigate={closeMobileMenu}
                />
              ))}

              {!isLandingPage && (
                <>
                  <DropdownMenu.Separator className="DropdownMenuSeparator" />
                  <DropdownMenuLabel text="Docs & learn" />
                  <NavDropdownLink
                    href={DOCS_NAV_HUB}
                    label="Browse all docs"
                    isActive={pathname === DOCS_NAV_HUB}
                    onNavigate={closeMobileMenu}
                  />
                  {DOCS_NAV_COLUMNS.map((column) => (
                    <React.Fragment key={column.label}>
                      <DropdownMenuLabel text={column.label} />
                      {column.items.map((item) => (
                        <NavDropdownLink
                          key={item.href}
                          href={item.href}
                          label={item.label}
                          isActive={pathname === item.href}
                          onNavigate={closeMobileMenu}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </>
              )}

              {!isLandingPage && (
                <>
                  <DropdownMenu.Separator className="DropdownMenuSeparator" />
                  <DropdownMenuLabel text="Features" />
                  <NavDropdownLink
                    href={FEATURES_NAV_HUB}
                    label="Explore all features"
                    isActive={pathname === FEATURES_NAV_HUB}
                    onNavigate={closeMobileMenu}
                  />
                  {FEATURES_NAV_COLUMNS.map((column) => (
                    <React.Fragment key={column.label}>
                      <DropdownMenuLabel text={column.label} />
                      {column.items.map((item) => (
                        <NavDropdownLink
                          key={item.href}
                          href={item.href}
                          label={item.label}
                          isActive={pathname === item.href}
                          onNavigate={closeMobileMenu}
                        />
                      ))}
                    </React.Fragment>
                  ))}

                  <DropdownMenu.Separator className="DropdownMenuSeparator" />
                  <DropdownMenuLabel text="Use cases" />
                  <NavDropdownLink
                    href="/use-cases"
                    label="All use cases"
                    isActive={pathname === '/use-cases'}
                    onNavigate={closeMobileMenu}
                  />
                  {useCaseMenuItems.map((item) => (
                    <NavDropdownLink
                      key={item.slug}
                      href={item.href}
                      label={item.name}
                      isActive={pathname === item.href}
                      onNavigate={closeMobileMenu}
                    />
                  ))}
                </>
              )}

              <DropdownMenu.Separator className="DropdownMenuSeparator" />
              <DropdownMenuLabel text="Legal" />

              {legalMenuItems.map((item) => (
                <NavDropdownLink
                  key={item.path}
                  href={item.path}
                  label={item.label}
                  isActive={pathname === item.path}
                  onNavigate={closeMobileMenu}
                />
              ))}

              {isSM && (
                <>
                  {!isLandingPage && (
                    <>
                      <DropdownMenu.Separator className="DropdownMenuSeparator" />
                      <DropdownMenuLabel text='Preferences' />

                      <View style={styles.preferencesContainer}>
                        <View style={styles.preferenceItem}>
                          <Text size='n' color='primary' style={styles.preferenceLabel}>Theme</Text>
                          <ThemeSelector showAccentSelector={false} />
                        </View>
                      </View>
                    </>
                  )}
                  {
                    !isAuthenticated && (
                      <>
                        <DropdownMenu.Separator className="DropdownMenuSeparator" />
                        <DropdownMenuItem
                          title={t('common.signIn')}
                          icon="arrow-right-to-bracket" 
                          onSelect={() => handleNavigation('/login')}
                        />
                      </>
                    )
                  }
                </>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        )
      }

      {
        isAuthenticated && (
          <Button
            text='Dashboard'
            variant="primary"
            size={isXS || isSM ? 'small' : 'medium'}
            href='/app'
            isRound={true}
          />
        )
      }
      {
        !isAuthenticated && (
          <>
            {isSM && windowWidth >= 360 && (
              <Button
                text={t('common.signIn')}
                variant="nav"
                size="small"
                textWeight={PUBLIC_NAV_TEXT_WEIGHT}
                onPress={() => handleAuth('login')}
                isRound={true}
                dataSet={{ publicNavButton: 'true' }}
              />
            )}
            <Button
              text="Sign up for free"
              variant="purple"
              isHollow={isLandingPage}
              hoverBackgroundColor={isLandingPage ? (isDarkTheme(theme) ? '#1C1C1C' : '#F5F5F5') : undefined}
              dataSet={isLandingPage ? { landingNavSignup: isDarkTheme(theme) ? 'dark' : 'light' } : undefined}
              size={isXS || isSM ? 'small' : 'medium'}
              textWeight={PUBLIC_NAV_TEXT_WEIGHT}
              onPress={() => {
                trackMarketingEvent(MARKETING_ACTION.CTA_CLICKED, 'public_nav/signup');
                router.push('/signup');
              }}
              isRound={true}
            />
          </>
        )
      }
    </View>
  );
});

NavigationButtonsRight.displayName = 'NavigationButtonsRight';

const HEADER_MAX_EXPANDED = LAYOUT.max;
const HEADER_MAX_COLLAPSED = 1150;
/** Scroll distance (px) over which the header eases from expanded → collapsed. */
const HEADER_SHRINK_RANGE = 160;

const readScrollY = (target) => {
  if (target && target !== document && target !== window && typeof target.scrollTop === 'number') {
    return target.scrollTop;
  }
  if (typeof document === 'undefined') return 0;
  const landing = document.querySelector('[data-landing-scroll]');
  if (landing) return landing.scrollTop || 0;
  return window.scrollY || document.documentElement.scrollTop || 0;
};

// Main layout component
const GlassHeader = React.memo(() => {
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const hasEditorialHeader = ['/', '/about', '/brand', '/pricing', '/business', '/api', '/mac', '/extension', '/based-benchmark'].includes(pathname);
  const landingColors = pathname === '/pricing' ? pricingPalette(isDarkTheme(theme)) : pathname === '/api' ? apiPalette(isDarkTheme(theme)) : landingPalette(isDarkTheme(theme));
  const [headerMaxWidth, setHeaderMaxWidth] = useState(HEADER_MAX_EXPANDED);

  useEffect(() => {
    if (Platform.OS !== 'web' || isXS || hasEditorialHeader) {
      setHeaderMaxWidth(HEADER_MAX_EXPANDED);
      return undefined;
    }

    let raf = 0;
    let latestY = 0;

    const apply = () => {
      raf = 0;
      const progress = Math.min(1, Math.max(0, latestY / HEADER_SHRINK_RANGE));
      const next = Math.round(
        HEADER_MAX_EXPANDED - (HEADER_MAX_EXPANDED - HEADER_MAX_COLLAPSED) * progress
      );
      setHeaderMaxWidth((prev) => (prev === next ? prev : next));
    };

    const onScroll = (event) => {
      const target = event?.target;
      // Ignore horizontal-only scrollers (tab strips, carousels).
      if (
        target &&
        target !== document &&
        target !== window &&
        target.scrollHeight <= target.clientHeight + 1 &&
        target.scrollWidth > target.clientWidth + 1
      ) {
        return;
      }
      latestY = readScrollY(target);
      if (raf) return;
      raf = requestAnimationFrame(apply);
    };

    latestY = readScrollY(null);
    apply();

    // Capture phase so nested page ScrollViews (landing, pricing, about, …) all drive the header.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('scroll', onScroll);
    };
  }, [isXS, pathname, hasEditorialHeader]);

  return (
    <View style={{ flex: 1, alignItems: 'center' }} pointerEvents="box-none">
      <View
        style={{
          marginTop: Math.max(isXS ? 10 : 20, insets.top + (isXS ? 6 : 10)),
          height: isXS ? 48 : isSM ? 50 : 52,
          borderRadius: 9999,
          overflow: 'hidden',
          maxWidth: isXS ? '100%' : headerMaxWidth,
          width: isXS ? 'calc(100% - 16px)' : isSM ? 'calc(100% - 24px)' : 'calc(100% - 32px)',
          alignSelf: 'center',
          borderWidth: Platform.OS === 'web' ? 1 : StyleSheet.hairlineWidth,
          borderColor: isDarkTheme(theme) ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
          marginLeft: insets.left,
          marginRight: insets.right,
          // Subtle drop-shadow lifts the pill off the content beneath so
          // labels stay legible while scrolling.
          ...(Platform.OS === 'web' ? {
            boxShadow: isDarkTheme(theme)
              ? '0 4px 20px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.3)'
              : '0 4px 20px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)',
          } : {}),
          ...(hasEditorialHeader ? {
            marginTop: insets.top,
            height: isXS ? 64 : 76,
            width: '100%',
            maxWidth: '100%',
            borderRadius: 0,
            borderWidth: 0,
            borderBottomWidth: 1,
            borderColor: landingColors.rule,
            backgroundColor: landingColors.page,
            boxShadow: 'none',
          } : {}),
        }}
      >
        {!hasEditorialHeader && <BlurView
          intensity={Platform.OS === 'web' ? 40 : 40}
          tint={isDarkTheme(theme) ? 'dark' : 'light'}
          style={{
            ...StyleSheet.absoluteFillObject,
            ...Platform.select({
              web: {
                backdropFilter: 'blur(24px) saturate(1.9)',
                WebkitBackdropFilter: 'blur(24px) saturate(1.9)',
              },
            }),
            // Higher opacity background so labels stay readable over
            // varied content beneath. ~0.8 is the Apple/Linear sweet spot.
            backgroundColor: isDarkTheme(theme)
              ? 'rgba(14,14,16,0.82)'
              : 'rgba(255,255,255,0.82)',
          }}
        />}
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: hasEditorialHeader ? (isXS ? 16 : 28) : isSM ? 10 : 12,
            ...(hasEditorialHeader ? { width: '100%', maxWidth: LAYOUT.max, alignSelf: 'center' } : {}),
          }}
        >
          {/* The left group is the one that gives: the auth buttons on the
              right must stay at full size, so only this side may compress. */}
          <View style={{ flexShrink: 1, minWidth: 0 }}>
            <NavigationButtonsLeft />
          </View>
          <View style={{ flexShrink: 0 }}>
            <NavigationButtonsRight />
          </View>
        </View>
      </View>
    </View>
  );
});

GlassHeader.displayName = 'GlassHeader';

const PublicLayoutInner = () => {
  const { colors, theme } = useTheme();
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const normalizedPathname = String(pathname || '').replace(/\/+$/, '') || '/';


  const screenOptions = useMemo(() => ({
    title: 'Gab AI',
    headerTitleAlign: 'center',
    headerTintColor: colors.text_color_primary,
    headerStyle: Platform.select({
      web: {
        height: 64 + insets.top,
        backgroundColor: 'transparent',
        borderBottomWidth: 0,
      },
      default: {
        borderBottomWidth: 0,
        backgroundColor: 'transparent',
      },
    }),
    headerTransparent: true,
    headerShadowVisible: false,
    header: () => <GlassHeader />,
    contentStyle: { 
      backgroundColor: colors.solid_color_primary,
      paddingBottom: insets.bottom,
    },
    animation: 'fade',
  }), [colors, theme, insets]);

  // Add global styles
  useGlobalStyles();

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <View style={{ 
        flex: 1, 
        backgroundColor: colors.solid_color_primary,
        paddingLeft: insets.left,
        paddingRight: insets.right,
      }}>
        <Stack screenOptions={screenOptions}>
        <Stack.Screen 
          name="index"
        />
        <Stack.Screen name="features" />
        <Stack.Screen name="download" />
        <Stack.Screen name="mac" />
        <Stack.Screen name="extension" />
        <Stack.Screen name="voice" />
        <Stack.Screen name="free-ai-chat" />
        <Stack.Screen name="alternatives" />
        <Stack.Screen name="chatgpt-alternative" />
        <Stack.Screen name="poe-alternative" />
        <Stack.Screen name="monica-alternative" />
        <Stack.Screen name="chathub-alternative" />
        <Stack.Screen name="typingmind-alternative" />
        <Stack.Screen name="t3chat-alternative" />
        <Stack.Screen name="galaxy-ai-alternative" />
        <Stack.Screen name="openrouter-alternative" />
        <Stack.Screen name="about" />
        <Stack.Screen name="brand" />
        <Stack.Screen name="based-benchmark" />
        <Stack.Screen name="switch" />
        <Stack.Screen name="contact" />
        <Stack.Screen name="feedback" />
        <Stack.Screen name="pricing" />
        <Stack.Screen name="business" />
        <Stack.Screen name="terms-of-sale" />
        <Stack.Screen name="terms-of-service" />
        <Stack.Screen name="privacy-policy" />
        <Stack.Screen name="refund-policy" />
        <Stack.Screen name="llms" />
        <Stack.Screen name="enterprise" />
        <Stack.Screen name="api" />
        <Stack.Screen 
          name="docs" 
          options={{ 
            headerShown: false,
            header: () => null,
          }} 
        />
        <Stack.Screen 
          name="learn" 
          options={{ 
            headerShown: false,
            header: () => null,
          }} 
        />
        <Stack.Screen name="blog" />
        </Stack>
        {/* Lazy host — see ExploreLightboxHost.jsx for duplicate-mount strategy. */}
        <ExploreLightboxHost skipOnDocsLearn />
        <ModalRoot />
        <ToastRoot />
      </View>
    </GestureHandlerRootView>
  );
};

export default PublicLayoutInner;
